/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import Breadcrumbs from '@Components/Breadcrumbs';
import Loader from '@Components/Loader';
import { useSouls } from '@Hooks/useSouls';
import { Link } from 'react-router-dom';

export default function Souls() {
  const { data: souls, status } = useSouls();

  // Group: top-level agents first, each followed by its subagents (children).
  // Children whose parent is missing/deleted are appended at the end as orphans.
  const grouped = (() => {
    if (!souls?.length) return [];
    const topLevel = souls.filter((s) => !s.parentSoulId);
    const childrenByParent = new Map<string, typeof souls>();
    for (const s of souls) {
      if (!s.parentSoulId) continue;
      const list = childrenByParent.get(s.parentSoulId) ?? [];
      list.push(s);
      childrenByParent.set(s.parentSoulId, list);
    }
    const knownParentIds = new Set(topLevel.map((s) => s.id));
    const orphans = souls.filter(
      (s) => s.parentSoulId && !knownParentIds.has(s.parentSoulId),
    );
    const rows: Array<{ soul: (typeof souls)[number]; depth: number }> = [];
    for (const parent of topLevel) {
      rows.push({ soul: parent, depth: 0 });
      for (const child of childrenByParent.get(parent.id) ?? []) {
        rows.push({ soul: child, depth: 1 });
      }
    }
    for (const orphan of orphans) {
      rows.push({ soul: orphan, depth: 1 });
    }
    return rows;
  })();

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/agents' },
          { name: 'Agents', href: '/agents' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Agents</Heading>
          <p>Manage AI agent configurations.</p>
        </div>
        <div className="flex justify-end">
          <Button asChild>
            <Link to="/agents/create">Create</Link>
          </Button>
        </div>
      </div>
      <Card>
        <CardContent>
          {status === 'pending' ? (
            <Loader size="lg" />
          ) : !souls?.length ? (
            <div className="py-8 text-center text-sm text-zinc-500">
              No agents configured yet. Create one to get started.
            </div>
          ) : (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Tools</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grouped.map(({ soul, depth }) => (
                  <TableRow key={soul.id}>
                    <TableCell>
                      <div
                        className="flex items-center gap-2"
                        style={{ paddingLeft: depth * 16 }}
                      >
                        {depth > 0 && <span className="text-zinc-400">↳</span>}
                        <Button variant="link" className="px-0!" asChild>
                          <Link to={`/agents/${soul.id}`}>{soul.name}</Link>
                        </Button>
                        {depth > 0 && (
                          <Badge variant="outline" className="text-xs">
                            subagent
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {soul.model ? (
                        <Badge variant="outline">{soul.model}</Badge>
                      ) : (
                        <span className="text-zinc-400">default</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {soul.enabledTools?.length ?? 0} tools
                    </TableCell>
                    <TableCell>
                      {soul.isDefault && (
                        <Badge variant="secondary">Default</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(soul.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
